import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Tag,
  HStack,
  Text,
  Input,
  Select,
  Flex,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";
import SendEmail from "../../../components/SendEmail";

const statusOptions = [
  { label: "대기", value: "Pending" },
  { label: "승인", value: "Approved" },
  { label: "거절", value: "Rejected" },
];

const LicenseVideos = () => {
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [inputPage, setInputPage] = useState("1");
  const toast = useToast();

  // 데이터 불러오기
  const fetchData = async () => {
    setLoading(true);
    const { data: res, error } = await supabase.rpc("get_permission_videos", {
      status: status || "Pending",
      searchword: search || null,
      page,
    });
    if (!error && res) {
      setData(res.data || []);
      setTotalPages(res.total_pages || 1);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [status, search, page]);

  // page가 바뀔 때 inputPage도 동기화
  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  // 승인/거절 처리
  const handleStatusChange = async (id, newStatus, row) => {
    const { error } = await supabase
      .from("shorts_permission_videos")
      .update({ permission_status: newStatus })
      .eq("id", id);
    if (!error) {
      // 승인 시 이메일 전송
      if (newStatus === "Approved") {
        try {
          await SendEmail({
            email: row.user_email,
            subject: `[승인] 쇼츠 라이센스 비디오 등록 승인 / ${row.video_title}`,
            message: `
              <p>안녕하세요, 플플입니다.</p>
              <p>요청하신 쇼츠 라이센스 비디오 등록이 승인되었습니다.</p>
              <p></p>
              <p>승인된 콘텐츠 정보:</p>
              <p>- 콘텐츠명: ${row.video_title}</p>
              <p>- 채널: ${row.channel_names}</p>
              <p></p>
              <p>승인된 콘텐츠는 이제 영상 라이센스 신청하기에서 확인하실 수 있습니다.</p>
              <p>문의사항이 있으시면 contact@plpl.kr로 연락주시기 바랍니다.</p>
            `,
            storageBucket: "modify",
          });
        } catch (emailError) {
          console.error("Error sending email:", emailError);
          toast({
            title: "이메일 전송 실패",
            description: "승인은 완료되었으나 이메일 전송에 실패했습니다.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }
      }

      toast({
        title: `상태가 ${
          newStatus === "Approved" ? "승인" : "거절"
        }되었습니다.`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchData();
    } else {
      toast({
        title: "상태 변경 실패",
        description: error.message,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // 페이지 직접 입력
  const handlePageInput = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) setInputPage(value);
  };
  const handlePageGo = () => {
    const num = parseInt(inputPage, 10);
    if (num >= 1 && num <= totalPages) setPage(num);
    else
      toast({ title: "올바른 페이지 번호를 입력하세요.", status: "warning" });
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
    setInputPage("1");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box maxW="1200px" mx="auto" py={8}>
      <Flex mb={4} gap={2} align="center">
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          width="120px"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <Input
          placeholder="이메일, 콘텐츠명, OTT명 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleKeyPress}
          width="300px"
        />
        <Button colorScheme="blue" onClick={handleSearch}>
          검색
        </Button>
      </Flex>
      <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="md">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>등록 신청자</Th>
              <Th>신청 콘텐츠</Th>
              <Th>OTT</Th>
              <Th>채널</Th>
              <Th>등록 신청일</Th>
              <Th>상태</Th>
              <Th>관리</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={7}>
                  <Spinner />
                </Td>
              </Tr>
            ) : data.length === 0 ? (
              <Tr>
                <Td colSpan={7}>
                  <Text textAlign="center" color="gray.400">
                    데이터 없음
                  </Text>
                </Td>
              </Tr>
            ) : (
              data.map((row) => {
                const channelNames = row.channel_names
                  ? row.channel_names.split(",").map((s) => s.trim())
                  : [];
                const channelUrls = row.channel_urls
                  ? row.channel_urls.split(",").map((s) => s.trim())
                  : [];
                return (
                  <Tr key={row.permission_id}>
                    <Td>
                      <Text>{row.user_name || row.user_email}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {row.user_email}
                      </Text>
                    </Td>
                    <Td>
                      <Text
                        color={
                          row.video_ott === "shortcha"
                            ? "red.500"
                            : row.video_ott === "watcha"
                            ? "blue.500"
                            : undefined
                        }
                      >
                        {row.video_title}
                      </Text>
                    </Td>
                    <Td>{row.video_ott}</Td>
                    <Td>
                      {channelNames.map((name, idx) => (
                        <Box key={idx}>
                          {channelUrls[idx] ? (
                            <a
                              href={channelUrls[idx]}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#3182ce",
                                textDecoration: "underline",
                              }}
                            >
                              {name}
                            </a>
                          ) : (
                            name
                          )}
                        </Box>
                      ))}
                    </Td>
                    <Td>
                      {row.created_date ? row.created_date.slice(0, 10) : "-"}
                    </Td>
                    <Td>
                      <Tag
                        colorScheme={
                          row.permission_status === "Approved"
                            ? "green"
                            : row.permission_status === "Rejected"
                            ? "red"
                            : "blue"
                        }
                      >
                        {row.permission_status === "Approved"
                          ? "승인"
                          : row.permission_status === "Rejected"
                          ? "거절"
                          : "대기"}
                      </Tag>
                    </Td>
                    <Td>
                      <HStack>
                        <Button
                          colorScheme="blue"
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleStatusChange(
                              row.permission_id,
                              "Approved",
                              row
                            )
                          }
                          isDisabled={row.permission_status === "Approved"}
                        >
                          승인
                        </Button>
                        <Button
                          colorScheme="red"
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleStatusChange(
                              row.permission_id,
                              "Rejected",
                              row
                            )
                          }
                          isDisabled={row.permission_status === "Rejected"}
                        >
                          거절
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>
      {/* 페이지네이션 */}
      <Flex justify="center" mt={4} gap={2} align="center">
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          이전
        </Button>
        <Input
          value={inputPage}
          onChange={handlePageInput}
          onBlur={handlePageGo}
          size="sm"
          width="50px"
          textAlign="center"
          fontSize="sm"
          placeholder="페이지"
        />
        <Button size="sm" onClick={handlePageGo}>
          이동
        </Button>
        <Text fontSize="sm" px={2}>
          / {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          다음
        </Button>
      </Flex>
      {/* 리스트 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>상세 리스트</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRow && (
              <>
                <Text fontWeight="bold" mb={2}>
                  신청자: {selectedRow.user_name || selectedRow.user_email}
                </Text>
                <Text mb={2}>콘텐츠:</Text>
                <Box mb={2}>
                  {selectedRow.licensed_video &&
                    selectedRow.licensed_video.map((v) => (
                      <Box key={v.id} mb={1}>
                        <b>{v.title}</b> ({v.ott})
                      </Box>
                    ))}
                </Box>
                <Text mb={2}>채널:</Text>
                <Box>
                  {selectedRow.channel &&
                    selectedRow.channel.map((c) => (
                      <Box key={c.id} mb={1}>
                        <a
                          href={c.channel_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#3182ce",
                            textDecoration: "underline",
                          }}
                        >
                          {c.channel_title}
                        </a>
                      </Box>
                    ))}
                </Box>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LicenseVideos;
